import * as dicomParser from 'dicom-parser';
import { uploadBytesResumable } from "firebase/storage";

/*
 * DICOM fields to de-identify.
 */
export const DICOM_FIELDS = [
    ["x00100010", 'PN', ''],
    ["x00100020", 'LO', ''],
    ["x00100030", 'DA', ''],
    ["x00100040", 'CS', ''],
    ["x00101010", 'AS', ''],
    ["x00101020", 'DS', ''],
    ["x00101030", 'DS', ''],
    ["x00101040", 'CS', ''],
];

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

function pad(num, size) {
    let s = String(num);
    while (s.length < size) s = "0" + s;
    return s;
}

function makeRandomString(length) {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    return Array.from({ length }, () =>
        chars.charAt(Math.floor(Math.random() * chars.length))
    ).join("");
}

function makeRandomDecimalString(length) {
    return Array.from({ length }, () => Math.floor(Math.random() * 10)).join("");
}


// ---------------------------------------------------------------------------
// Public helpers
// ---------------------------------------------------------------------------

/**
 * Generates a de-identified replacement value for a given DICOM VR type.
 * @param {number} length
 * @param {string} vr - DICOM Value Representation
 * @returns {string}
 */
export function makeDeIdentifiedValue(length, vr) {
    if (['LO', 'SH', 'PN', 'CS'].includes(vr)) return makeRandomString(length);
    if (vr === 'DS')  return makeRandomDecimalString(length);
    if (vr === 'AS')  return '000Y';
    if (vr === 'DA') {
        const now = new Date();
        return `${now.getFullYear()}${pad(now.getMonth() + 1, 2)}${pad(now.getDate(), 2)}`;
    }
    if (vr === 'TM') {
        const now = new Date();
        return `${pad(now.getHours(), 2)}${pad(now.getMinutes(), 2)}${pad(now.getSeconds(), 2)}`;
    }
    return '';
}

/**
 * Overwrites PII fields in a dicom-parser DataSet in-place, converts it to a
 * Blob, and kicks off a resumable Firebase Storage upload.
 *
 * @param {object}   dataSet    - dicom-parser DataSet
 * @param {object}   storageRef - Firebase StorageReference
 * @param {object}   callbacks
 * @param {Function} [callbacks.onProgress]     - (snapshot) => void
 * @param {Function} [callbacks.onComplete]     - () => void
 * @param {Function} [callbacks.onError]        - (error) => void
 * @param {Function} [callbacks.onTaskCreated]  - (uploadTask) => void, lets the caller track/cancel the resumable task
 */
export function deIdentifyAndUpload(dataSet, storageRef, sessionFields, { onProgress, onComplete, onError, onTaskCreated } = {}) {
    sessionFields.forEach(attr => {
        const element = dataSet.elements[attr[0]];
        if (!element) return;

        const str = dataSet.string(attr[0]);
        if (str === undefined) return;


        const replacement = attr[2] !== '' ? attr[2] : makeDeIdentifiedValue(str.length, attr[1]);
        attr[2] = replacement;

        for (let i = 0; i < element.length; i++) {
            dataSet.byteArray[element.dataOffset + i] =
                i < replacement.length ? replacement.charCodeAt(i) : 32;
        }
    });

    const blob = new Blob([dataSet.byteArray], { type: "application/dicom" });
    const uploadTask = uploadBytesResumable(storageRef, blob);
    onTaskCreated?.(uploadTask);

    uploadTask.on(
        'state_changed',
        (snapshot) => onProgress?.(snapshot),
        (error)    => onError?.(error),
        ()         => onComplete?.()
    );
}

/**
 * @param {File}     file
 * @param {object}   storageRef    - Firebase StorageReference
 * @param {Array}    sessionFields - mutable copy of DICOM_FIELDS for this upload session;
 *                                   shared across all files so cached values are reused.
 * @param {object}   callbacks     - { onProgress, onComplete, onError, onTaskCreated, shouldAbort }
 * @param {Function} [callbacks.shouldAbort] - () => boolean, checked right before the Storage upload starts;
 *                                              skips starting this file entirely when true (e.g. session cancelled)
 */
export function processAndUploadFile(file, storageRef, sessionFields, callbacks) {
    const reader = new FileReader();

    reader.onload = () => {
        const byteArray = new Uint8Array(reader.result);
        const kb = byteArray.length / 1024;
        const sizeLabel = kb > 1024
            ? `${(kb / 1024).toFixed(3)} MB`
            : `${kb.toFixed(0)} KB`;

        // Small delay keeps the main thread from blocking on large files
        setTimeout(() => {
            if (callbacks.shouldAbort?.()) {
                callbacks.onError?.(new Error('Upload aborted'));
                return;
            }
            try {
                const dataSet = dicomParser.parseDicom(byteArray);
                deIdentifyAndUpload(dataSet, storageRef, sessionFields, callbacks);
            } catch (err) {
                console.warn(`DICOM parse error (${sizeLabel}): ${err}`);
                callbacks.onError?.(err);
            }
        }, 30);
    };

    reader.readAsArrayBuffer(file);
}
